// Main App component - sets up routing and authentication
// This is the entry point of the React app

/**
 * 
 * NOTE SUMMARY - MAIN ROUTER
 * 
 * What this file does:
 *  - Sets up React Router (navigation between pages)
 *  - Defines all routes (/login, /dashboard, /leads, /henri)
 *  - Protects routes (must be logged in to access)
 *  - Redirects to login if not authenticated
 *  - Default route goes to dashboard
 * 
 */



import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { isAuthenticated } from './utils/auth';
import Login from './pages/Login';
import ExecutiveDashboard from './pages/ExecutiveDashboard';
import LeadManagement from './pages/LeadManagement';
import HenriDashboard from './pages/HenriDashboard';
import { StatusWidget } from './components/SupportWidget';
import Pipeline from './pages/Pipeline';
import NewsletterSubscribers from './pages/NewsletterSubscribers';
import './styles/App.css';
import './styles/components.css';

// Protected Route component
// Redirects to login if not authenticated
function ProtectedRoute({ children }) {
  // TEMPORARY DEV MODE: Bypass authentication for local development
  const isDevelopment = window.location.hostname === 'localhost';
  return (isDevelopment || isAuthenticated()) ? children : <Navigate to="/login" />;
}

function App() {
  return (
    <Router>
      <Routes>
        {/* Public route - Login */}
        <Route path="/login" element={<Login />} />
        
        {/* Protected routes - Require authentication */}
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <ExecutiveDashboard />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/leads" 
          element={
            <ProtectedRoute>
              <LeadManagement />
            </ProtectedRoute>
          } 
        />
        
        <Route
          path="/pipeline"
          element={
            <ProtectedRoute>
              <Pipeline />
            </ProtectedRoute>
          }
        />

        <Route
          path="/henri"
          element={
            <ProtectedRoute>
              <HenriDashboard />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/newsletter" 
          element={
            <ProtectedRoute>
              <NewsletterSubscribers />
            </ProtectedRoute>
          } 
        />
        
        {/* Default route - redirect to dashboard */}
        <Route path="/" element={<Navigate to="/dashboard" />} />
        
        {/* 404 - Not found */}
        <Route path="*" element={<div>Page not found</div>} />
      </Routes>
      
      {/* Status Widget - appears on all pages when authenticated */}
      {(window.location.hostname === 'localhost' || isAuthenticated()) && (
        <StatusWidget 
          config={{
            companyName: 'Shoofly Intelligence Cloud',
            clientName: 'Plan Participant',
            logoUrl: 'https://i.imgur.com/BkCXJm9.png',
            supportEmail: 'admin@shooflyai.com',
            webhookUrl: 'https://hook.us1.make.com/1jk6i78r1powws6knisw9xa81nw3zqh4',
            healthCheckEndpoint: window.location.hostname === 'localhost' 
              ? 'http://localhost:8888/.netlify/functions/health-check'
              : '/.netlify/functions/health-check',
            healthCheckInterval: 60000, // 60 seconds - best practice balance
            brandColor: '#1E3A8A',
            position: 'bottom-right',
            enabled: true,
            // Docs tab - Crown Mark Unified Docs
            docsEnabled: true,
            docsIframeUrl: 'https://pp-docs-assistant-2025.netlify.app/'
          }}
        />
      )}
    </Router>
  );
}

export default App;
